import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { SxProps } from '@mui/system';
import {
  Stack,
  Grid,
  Button,
  IconButton,
  TypographyProps,
  FormControl,
  Typography,
  Theme,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  createFilterOptions,
  Checkbox,
  FormControlLabel,
  Fade,
  Box,
  ChipProps,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';

import { questionDifficultyOptions } from '../../../constants';
import { getSubTopicState, ISubTopic } from '../../../store/SubTopicSlice';
import { getTagsList, getTagsState } from '../../../store/TagsSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { useAppSelector } from '../../../hooks';
import {
  checkUserPermission,
  getQuestionDifficultyChipColor,
} from '../../../helper';
import CustomGrayContainer from '../../../components/Containers/CustomGrayContainer';
import AddNewSubTopicDrawer from './components/SubTopicManager/AddNewSubTopicDrawer';
import DetailViewCard from '../../../components/Card/DetailViewCard';

import './styles.css';
import GenerateQuestionModal from './StructuredEssayQuestions/modals/GenerateQuestionModal';
import {
  adminCreateNewStructuredQuestion,
  approveStructuredEssayQuestion,
  deleteStructuredEssayQuestion,
  getStructuredEssayQuestionById,
  getStructuredEssayQuestionList,
  getStructuredEssayQuestionState,
  IAdminCreateStructuredQuestion,
  IStructuredEssayQuestion,
  ISubQuestion,
  IUpdateStructuredQuestion,
  resetQuestionList,
  TQuestionDifficulty,
  updateStructuredEssayQuestion,
} from '../../../store/StructuredEssayQuestionSlice';
import SubQuestionsManager from './StructuredEssayQuestions/SubQuestionsManager';
import MainQuestionManager from './StructuredEssayQuestions/MainQuestionManager';
import QuestionCard from './components/QuestionCard';
import ChangeCriteriaModal from './StructuredEssayQuestions/modals/ChangeCriteriaModal';
import SimpleConfirmationAlert from '../../../components/Alert/SimpleConfirmationAlert';
import { getTenantsState } from '../../../store/V2/TenantsSlice';

export interface INewSubTopicItem {
  curriculum: string;
  grade: string;
  subject: string;
}

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

const inputStyles: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderRadius: '4px',
};

const divStyles = {
  display: 'inline-block',
  borderRadius: '7px',
  padding: '2px',
  background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
  marginRight: '8px',
  align: 'center',
};

const aiBtnProps: SxProps = {
  borderRadius: '5px',
  backgroundColor: '#f9f7fd',
  color: '#004280',
  padding: '4px 14px',
  fontSize: '14px',
  '&:hover': {
    backgroundColor: '#f0f4ff',
  },
};

const filter = createFilterOptions();

const StructuredEssayQuestions: React.FC = () => {
  const UrlParams = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const questionTagState = useAppSelector(getTagsState);
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const structuredEssayQuestionState = useAppSelector(
    getStructuredEssayQuestionState
  );

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // Permissions
  const questionCreatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['create'],
    },
  });

  const questionUpdatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['edit'],
    },
  });

  const questionApprovePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['approve'],
    },
  });

  const questionDeletePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuestion: ['delete'],
    },
  });

  // State
  const [questionSource, setQuestionSource] = useState('');
  const [questionTags, setQuestionTags] = useState<string[]>([]);
  const [
    isGenerateQuestionModalOpen,
    handleIsGenerateQuestionModalOpen,
  ] = useState(false);
  const [
    isGenerateQuizChangeCriteriaModalOpen,
    handleIsGenerateQuizChangeCriteriaModalOpen,
  ] = useState(false);
  const [subTopics, setSubTopics] = useState<
    Array<{ subTopic: ISubTopic; paper: string }>
  >([]);

  const [questionTagsInputValue, setQuestionTagsInputValue] = useState<string>(
    ''
  );

  const [
    isAddNewSubTopicDrawerVisible,
    setAddNewSubTopicDrawerVisibility,
  ] = useState(false);

  const [messageAlertData, setMessageAlertData] = useState<
    | {
        open: boolean;
        title: string;
        description?: string;
      }
    | undefined
  >(undefined);

  const [deleteQuestionAlertData, setDeleteQuestionAlertData] = useState<
    | {
        open: boolean;
        value: string;
      }
    | undefined
  >(undefined);

  const [
    deleteSubTopicItemAlertData,
    setDeleteSubTopicItemAlertData,
  ] = useState<
    | {
        open: boolean;
        value: string;
      }
    | undefined
  >(undefined);

  const [questionData, setQuestionData] = useState<IStructuredEssayQuestion[]>([
    {
      mainQuestion: '',
      subQuestions: [],
      totalMarks: 0,
      difficulty: questionDifficultyOptions[0],
      subTopics: [],
      tags: [],
      source: '',
    },
  ]);

  const subId = UrlParams.subTopicId;
  const questyStringParams = new URLSearchParams(location.search);
  const urlQuestionId = questyStringParams.get('questionId') || '';
  const questionType = questyStringParams.get('type') || '';

  const [displayedQuestions, setDisplayedQuestions] = useState<
    IStructuredEssayQuestion[]
  >([]);

  const [selectedQuestion, setSelectedQuestion] = useState<
    IStructuredEssayQuestion | undefined
  >(undefined);

  const getInitialQuestion = (): IStructuredEssayQuestion => ({
    mainQuestion: '',
    subQuestions: [],
    totalMarks: 0,
    difficulty: questionDifficultyOptions[0],
    subTopics: [],
    tags: [],
    source: '',
  });

  const resetQuestions = () => {
    setSelectedQuestion(undefined);
    clearQuestionData();
    setQuestionData([getInitialQuestion()]);
    setQuestionSource('');
    setQuestionTags([]);
    setSubTopics([]);
    // Don't reset displayedQuestions here as it should only contain saved questions
  };

  useEffect(() => {
    if (
      (structuredEssayQuestionState.generatedQuestions.length > 0 &&
        structuredEssayQuestionState.threadId) ||
      urlQuestionId
    ) {
      // For generated questions, only update questionData
      if (structuredEssayQuestionState.threadId) {
        setQuestionData(structuredEssayQuestionState.generatedQuestions);
      } else {
        // For existing questions (not generated), update both
        setQuestionData(structuredEssayQuestionState.questions);
        setDisplayedQuestions(structuredEssayQuestionState.questions);
      }
    }
  }, [
    structuredEssayQuestionState.generatedQuestions,
    structuredEssayQuestionState.questions,
  ]);

  useEffect(() => {
    if (subId) {
      dispatch(resetQuestionList());
      dispatch(getStructuredEssayQuestionList(subId));
    }
  }, [subId]);

  useEffect(() => {
    setDisplayedQuestions([]);
    if (structuredEssayQuestionState.questions.length > 0 && subId) {
      const filtered = structuredEssayQuestionState.questions.filter((q) =>
        q.subTopics?.some((st) => st.subTopic._id === subId)
      );
      setDisplayedQuestions(filtered);
    }
  }, [structuredEssayQuestionState.questions, subId]);

  useEffect(() => {
    if (urlQuestionId && questionType === 'structured') {
      dispatch(getStructuredEssayQuestionById(urlQuestionId));
    }
  }, [urlQuestionId, questionType]);

  useEffect(() => {
    if (structuredEssayQuestionState.question) {
      setDisplayedQuestions((prev) => {
        return prev.map((q) => {
          if (q._id === structuredEssayQuestionState.question?._id) {
            return {
              ...q,
              isApproved: structuredEssayQuestionState.question?.isApproved,
              approvedBy: structuredEssayQuestionState.question?.approvedBy,
            };
          }
          return q;
        });
      });
    }
  }, [structuredEssayQuestionState.question]);

  useEffect(() => {
    if (urlQuestionId && structuredEssayQuestionState.question) {
      const fetchedQuestion = structuredEssayQuestionState.question;
      setSelectedQuestion(fetchedQuestion);

      setQuestionSource(fetchedQuestion.source || '');
      setQuestionTags(fetchedQuestion.tags || []);
      setSubTopics(fetchedQuestion.subTopics || []);

      setQuestionData((prev) => {
        const exists = prev.some((q) => q._id === fetchedQuestion._id);
        return exists ? prev : [...prev, fetchedQuestion];
      });

      setDisplayedQuestions((prev) => {
        const exists = prev.some((q) => q._id === fetchedQuestion._id);
        return exists ? prev : [...prev, fetchedQuestion];
      });
    }
  }, [structuredEssayQuestionState.question]);

  const clearQuestionData = () => {
    navigate(
      {
        search: undefined,
      },
      {
        replace: true,
      }
    );
  };

  const handleRemoveQuestion = (indexToRemove: number) => {
    setQuestionData((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  // Add new useEffect to handle API success
  useEffect(() => {
    if (
      structuredEssayQuestionState.status === 'idle' &&
      structuredEssayQuestionState.questions.length > 0
    ) {
      // Only reset if we have new questions added (indicating successful creation)
      const hasNewQuestions = structuredEssayQuestionState.questions.some(
        (q) => !displayedQuestions.some((dq) => dq._id === q._id)
      );

      if (hasNewQuestions) {
        resetQuestions();
      }
    }
  }, [
    structuredEssayQuestionState.status,
    structuredEssayQuestionState.questions,
  ]);

  if (!subTopicState.subTopic) {
    return <div />;
  }

  const _subTopicData = subTopicState.subTopic;
  const isSubmitDisabled =
    questionData.length === 0 ||
    subTopics.length === 0 ||
    questionData.some((question) => {
      // Check if main question is empty
      if (!question.mainQuestion?.trim()) {
        return true;
      }

      // Check if difficulty is not set
      if (!question.difficulty) {
        return true;
      }

      // Check if there are no sub-questions
      if (!question.subQuestions || question.subQuestions.length === 0) {
        return true;
      }

      // Check if total marks (sum of sub questions) is 0
      const totalMarks = question.subQuestions.reduce(
        (sum, sub) => sum + (Number(sub.marks) || 0),
        0
      );
      if (totalMarks === 0) {
        return true;
      }

      // Check if any sub-question is incomplete
      return question.subQuestions.some(
        (sub) =>
          !sub.question?.trim() ||
          sub.marks === undefined ||
          sub.marks === null ||
          Number.isNaN(Number(sub.marks)) ||
          sub.marks <= 0 ||
          sub.marks > 100 ||
          !sub.markingScheme?.trim()
      );
    });

  const validateSubmit = (): boolean => {
    if (!questionData || questionData.length === 0) {
      setMessageAlertData({
        open: true,
        title: 'Alert',
        description: 'Please add at least one question.',
      });
      return false;
    }

    if (subTopics.length === 0) {
      setMessageAlertData({
        open: true,
        title: 'Alert',
        description: 'Please select at least one paper.',
      });
      return false;
    }

    for (let i = 0; i < questionData.length; i += 1) {
      const question = questionData[i];

      if (!question.mainQuestion || question.mainQuestion.trim() === '') {
        setMessageAlertData({
          open: true,
          title: 'Alert',
          description: `Main question is required for question ${i + 1}.`,
        });
        return false;
      }

      if (!question.difficulty) {
        setMessageAlertData({
          open: true,
          title: 'Alert',
          description: `Difficulty level is required for question ${i + 1}.`,
        });
        return false;
      }

      if (!question.subQuestions || question.subQuestions.length === 0) {
        setMessageAlertData({
          open: true,
          title: 'Alert',
          description: `At least one sub-question is required for question ${
            i + 1
          }.`,
        });
        return false;
      }

      const totalSubMarks = question.subQuestions.reduce(
        (sum, sub) => sum + (Number(sub.marks) || 0),
        0
      );
      if (totalSubMarks === 0) {
        setMessageAlertData({
          open: true,
          title: 'Alert',
          description: `Total marks must be greater than 0 for question ${
            i + 1
          }.`,
        });
        return false;
      }

      for (let j = 0; j < question.subQuestions.length; j += 1) {
        const sub = question.subQuestions[j];
        if (!sub.question || sub.question.trim() === '') {
          setMessageAlertData({
            open: true,
            title: 'Alert',
            description: `Sub-question ${
              j + 1
            } is missing the question text in question ${i + 1}.`,
          });
          return false;
        }
        if (
          sub.marks === undefined ||
          sub.marks === null ||
          Number.isNaN(Number(sub.marks)) ||
          sub.marks <= 0 ||
          sub.marks > 100
        ) {
          setMessageAlertData({
            open: true,
            title: 'Alert',
            description: `Marks must be between 1 and 100 for sub-question ${
              j + 1
            } in question ${i + 1}.`,
          });
          return false;
        }
        if (!sub.markingScheme || sub.markingScheme.trim() === '') {
          setMessageAlertData({
            open: true,
            title: 'Alert',
            description: `Marking scheme is required for sub-question ${
              j + 1
            } in question ${i + 1}.`,
          });
          return false;
        }
      }
    }

    return true;
  };

  const handleApprove = () => {
    if (selectedQuestion) {
      if (selectedQuestion.isApproved === true) {
        dispatch(
          approveStructuredEssayQuestion({
            id: selectedQuestion._id || '',
            isApproved: false,
          })
        );
      } else {
        dispatch(
          approveStructuredEssayQuestion({
            id: selectedQuestion._id || '',
            isApproved: true,
          })
        );
      }
    }
  };

  const handleDeleteQuestion = () => {
    if (deleteQuestionAlertData) {
      dispatch(
        deleteStructuredEssayQuestion({
          id: deleteQuestionAlertData.value,
        })
      );

      setDeleteQuestionAlertData(undefined);
    }
  };

  const handleUpdateQuestion = () => {
    if (!selectedQuestion || !selectedQuestion._id) return;

    const questionToUpdate = questionData.find(
      (q) => q._id === selectedQuestion._id
    );

    if (!questionToUpdate) return;

    if (selectedQuestion && selectedQuestion._id) {
      const apiData: IUpdateStructuredQuestion = {
        id: selectedQuestion._id,
        mainQuestion: questionToUpdate.mainQuestion,
        difficulty: questionToUpdate.difficulty,
        totalMarks: questionToUpdate.totalMarks,
        subQuestions: questionToUpdate.subQuestions.map((sub) => ({
          question: sub.question,
          marks: sub.marks,
          markingScheme: sub.markingScheme,
          sortingOrder: sub.sortingOrder,
        })),
        subTopics: subTopics.map((st) => ({
          subTopic: st.subTopic._id,
          paper: st.paper,
        })),
      };

      if (questionSource.trim() !== '') {
        apiData.source = questionSource.trim();
      }

      if (questionTags.length > 0) {
        apiData.tags = questionTags;
      }

      dispatch(updateStructuredEssayQuestion(apiData));
    }
  };

  const handleCreateQuestion = () => {
    if (UrlParams.subTopicId) {
      const valid = validateSubmit();

      if (valid) {
        const questionsToCreate: IAdminCreateStructuredQuestion[] = questionData.map(
          (question) => {
            const mappedQuestion: IAdminCreateStructuredQuestion = {
              mainQuestion: question.mainQuestion,
              difficulty: question.difficulty,
              totalMarks: question.totalMarks,
              subQuestions: question.subQuestions.map((sub) => ({
                question: sub.question,
                marks: sub.marks,
                markingScheme: sub.markingScheme,
                sortingOrder: sub.sortingOrder,
              })),
              subTopics: subTopics.map((st) => ({
                subTopic: st.subTopic._id,
                paper: st.paper,
              })),
            };

            if (questionTags.length > 0) {
              mappedQuestion.tags = questionTags;
            }

            if (questionSource.trim() !== '') {
              mappedQuestion.source = questionSource.trim();
            }

            return mappedQuestion;
          }
        );

        dispatch(adminCreateNewStructuredQuestion(questionsToCreate));
      }
    }
  };

  const handleQuestionSubTopicCheckChange = (paperItem: { name: string }) => {
    let newSubTopics = [...subTopics];

    const isItemExist = subTopics.some(
      (questionSubTopic) =>
        questionSubTopic.subTopic._id === UrlParams.subTopicId &&
        questionSubTopic.paper === paperItem.name
    );

    if (isItemExist) {
      newSubTopics = newSubTopics.filter((questionSubTopic) => {
        if (
          questionSubTopic.subTopic._id === UrlParams.subTopicId &&
          questionSubTopic.paper === paperItem.name
        ) {
          return false;
        }

        return true;
      });
    } else {
      newSubTopics.push({
        subTopic: _subTopicData,
        paper: paperItem.name,
      });
    }

    setSubTopics(newSubTopics);
  };

  const handleAddNewOption = (questionIndex: number) => {
    setQuestionData((prevData) => {
      const updated = [...prevData];
      const subQuestions = [...(updated[questionIndex].subQuestions || [])];

      const newSubQuestion = {
        question: '',
        markingScheme: '',
        marks: 0,
        sortingOrder: subQuestions.length,
      };

      updated[questionIndex] = {
        ...updated[questionIndex],
        subQuestions: [...subQuestions, newSubQuestion],
      };

      return updated;
    });
  };

  const handleDeleteOption = (qIndex: number, subIndex: number) => {
    const updated = [...questionData];
    updated[qIndex].subQuestions.splice(subIndex, 1);
    setQuestionData(updated);
  };

  const handleUpdateContentOption = (
    questionIndex: number,
    subIndex: number,
    content: Partial<ISubQuestion>
  ) => {
    const updated = questionData.map((item, idx) => {
      if (idx === questionIndex) {
        return {
          ...item,
          subQuestions: item.subQuestions.map((subQ, subIdx) => {
            if (subIdx === subIndex) {
              return {
                ...subQ,
                ...content,
              };
            }
            return subQ;
          }),
        };
      }
      return item;
    });
    setQuestionData(updated);
  };

  const onQuestionChange = (question: IStructuredEssayQuestion) => {
    if (!urlQuestionId) {
      navigate({
        search: `?questionId=${question._id}&type=structured`,
      });
    } else if (question._id && urlQuestionId !== question._id) {
      questyStringParams.set('questionId', question._id);
      questyStringParams.set('type', 'structured');

      navigate({
        search: `?${questyStringParams.toString()}`,
      });
    }
  };

  const filterOutCurrentSubTopic = (subtopic: { subTopic: ISubTopic }) =>
    subtopic.subTopic._id !== UrlParams.subTopicId;

  return (
    <Fade in>
      <div>
        <div style={{ padding: '0 30px' }}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ pb: '15px' }}
          >
            {questionUpdatePermission && selectedQuestion && (
              <Button
                color="primary"
                variant="contained"
                onClick={handleUpdateQuestion}
                style={{ marginRight: '10px' }}
                disabled={
                  !selectedQuestion.mainQuestion?.trim() ||
                  !selectedQuestion.difficulty ||
                  subTopics.length === 0 ||
                  questionData
                    .find((q) => q._id === selectedQuestion._id)
                    ?.subQuestions.some(
                      (sub) =>
                        !sub.question?.trim() ||
                        sub.marks === undefined ||
                        sub.marks === null ||
                        Number.isNaN(Number(sub.marks)) ||
                        sub.marks <= 0 ||
                        sub.marks > 100 ||
                        !sub.markingScheme?.trim()
                    )
                }
              >
                Save
              </Button>
            )}
            {questionApprovePermission && selectedQuestion && (
              <Button
                color={selectedQuestion.isApproved ? 'secondary' : 'info'}
                variant="contained"
                onClick={handleApprove}
                style={{ marginRight: '10px' }}
              >
                {selectedQuestion.isApproved ? 'Unapprove' : 'Approve'}
              </Button>
            )}
            {questionDeletePermission && selectedQuestion && (
              <Button
                variant="contained"
                color="error"
                sx={{ mr: '10px' }}
                onClick={() =>
                  setDeleteQuestionAlertData({
                    open: true,
                    value: selectedQuestion._id || '',
                  })
                }
              >
                Delete
              </Button>
            )}
            {questionCreatePermission && !selectedQuestion && (
              <>
                {/* {!structuredEssayQuestionState.threadId && ( */}
                <div style={divStyles}>
                  <Button
                    color="inherit"
                    sx={aiBtnProps}
                    onClick={() => handleIsGenerateQuestionModalOpen(true)}
                  >
                    Generate with AI
                    <AutoAwesomeIcon
                      sx={{
                        ml: '10px',
                        fontSize: '20px',
                        color: '#0055a4',
                      }}
                    />
                  </Button>
                </div>
                {/* )} */}
                {/* {structuredEssayQuestionState.threadId && (
                  <div style={divStyles}>
                    <Button
                      color="inherit"
                      sx={aiBtnProps}
                      onClick={generateAnotherQuestion}
                    >
                      Generate Another Question
                    </Button>
                  </div>
                )} */}
                {/* {structuredEssayQuestionState.threadId && (
                  <div style={divStyles}>
                    <Button
                      color="inherit"
                      sx={aiBtnProps}
                      onClick={() => {
                        handleIsGenerateQuizChangeCriteriaModalOpen(true);
                        handleClose();
                      }}
                    >
                      Change Criteria
                    </Button>
                  </div>
                )} */}
                {/* {structuredEssayQuestionState.threadId && (
                  <div>
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      onClick={handleClick}
                      sx={{
                        mr: '10px',
                        padding: '6px',
                        background:
                          'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
                        borderRadius: '25%',
                      }}
                    >
                      <KeyboardDoubleArrowDownIcon
                        sx={{
                          color: 'white',
                          fontSize: 'large',
                        }}
                      />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      PaperProps={{
                        style: {
                          maxHeight: 48 * 4.5,
                          width: '20ch',
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleIsGenerateQuestionModalOpen(true);
                          handleClose();
                        }}
                      >
                        Change the start prompt
                      </MenuItem>
                    </Menu>
                  </div>
                )} */}
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isSubmitDisabled}
                  onClick={handleCreateQuestion}
                  style={{ marginRight: '10px' }}
                >
                  Create
                </Button>
              </>
            )}
          </Stack>
          <div>
            <Grid container spacing={2} sx={{ pb: '15px' }}>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                {questionCreatePermission && (
                  <Button
                    fullWidth
                    color="primary"
                    variant="outlined"
                    size="large"
                    onClick={resetQuestions}
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                    }}
                  >
                    Create a new Question
                  </Button>
                )}
                <div
                  style={{ margin: '15px 0' }}
                  className="question-list-item-container"
                >
                  {displayedQuestions.length
                    ? displayedQuestions.map((question, index) => {
                        const _selected =
                          question._id === selectedQuestion?._id;
                        const _title = `Question ${(index + 1)
                          .toString()
                          .padStart(2, '0')}`;
                        let _description = '';
                        if (question.mainQuestion) {
                          _description = question.mainQuestion.substring(
                            0,
                            100
                          );
                        }
                        const _chips: Array<ChipProps> = [
                          {
                            color: getQuestionDifficultyChipColor(
                              question.difficulty
                            ),
                            label: question.difficulty.toLowerCase(),
                          },
                        ];
                        if (question.isApproved) {
                          _chips.push({
                            color: 'info',
                            label: 'Approved',
                          });
                        }
                        return (
                          <QuestionCard
                            key={index}
                            isSelected={_selected}
                            title={_title}
                            description={_description}
                            chips={_chips}
                            onClick={() => onQuestionChange(question)}
                          />
                        );
                      })
                    : null}
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={8} lg={6}>
                {(selectedQuestion
                  ? questionData.filter((q) => q._id === selectedQuestion._id)
                  : questionData
                ).map((question, index) => (
                  <CustomGrayContainer
                    key={index}
                    sx={{ mb: 3, position: 'relative' }}
                  >
                    {!selectedQuestion && questionData.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveQuestion(index)}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    )}
                    <div style={{ overflowY: 'auto', paddingRight: '10px' }}>
                      <MainQuestionManager
                        mode="edit"
                        question={question.mainQuestion}
                        updateDetails={(markdown) => {
                          const updatedItems = questionData.map((item, i) => {
                            if (selectedQuestion?._id === item._id) {
                              return {
                                ...item,
                                ...{ mainQuestion: markdown },
                              };
                            }
                            if (index === i) {
                              return {
                                ...item,
                                ...{ mainQuestion: markdown },
                              };
                            }

                            return item;
                          });

                          setQuestionData(updatedItems);
                        }}
                      />

                      <SubQuestionsManager
                        subQuestions={question.subQuestions}
                        addNewOption={() => {
                          let indexToUpdate = index;
                          if (selectedQuestion?._id) {
                            questionData.forEach((item, i) => {
                              if (item._id === selectedQuestion._id) {
                                indexToUpdate = i;
                              }
                            });
                          }
                          handleAddNewOption(indexToUpdate);
                        }}
                        deleteOption={(subIndex) =>
                          handleDeleteOption(index, subIndex)
                        }
                        updateOption={(subIndex, content) => {
                          let indexToUpdate = index;
                          if (selectedQuestion?._id) {
                            questionData.forEach((item, i) => {
                              if (item._id === selectedQuestion._id) {
                                indexToUpdate = i;
                              }
                            });
                          }
                          handleUpdateContentOption(
                            indexToUpdate,
                            subIndex,
                            content
                          );
                        }}
                      />

                      <Box display="flex" gap={2} mt="30px" mb="10px">
                        <FormControl fullWidth sx={{ flex: 1 }}>
                          <Typography {...label}>
                            Select Difficulty Level
                          </Typography>
                          <Select
                            size="small"
                            sx={inputStyles}
                            value={question.difficulty}
                            onChange={(event) => {
                              const updatedItems = questionData.map((item) =>
                                item._id === question._id
                                  ? {
                                      ...item,
                                      difficulty: event.target
                                        .value as TQuestionDifficulty,
                                    }
                                  : item
                              );
                              setQuestionData(updatedItems);
                            }}
                          >
                            {questionDifficultyOptions.map(
                              (difficultyOption, i) => (
                                <MenuItem key={i} value={difficultyOption}>
                                  {difficultyOption}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ flex: 1 }}>
                          <Typography {...label}>Total Marks</Typography>
                          <TextField
                            size="small"
                            type="number"
                            disabled
                            InputProps={{
                              inputProps: {
                                max: 100,
                                maxLength: 100,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                              },
                              readOnly: true,
                            }}
                            sx={{
                              ...inputStyles,
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                              },
                              '& .MuiOutlinedInput-root.Mui-disabled': {
                                '& > fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                              },
                            }}
                            value={
                              question.subQuestions.reduce(
                                (sum, sub) => sum + (Number(sub.marks) || 0),
                                0
                              ) || ''
                            }
                            placeholder="Add Marks"
                          />
                        </FormControl>
                      </Box>
                    </div>
                  </CustomGrayContainer>
                ))}
                {!selectedQuestion && (
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setQuestionData([
                          ...questionData,
                          {
                            mainQuestion: '',
                            subQuestions: [],
                            totalMarks: 0,
                            difficulty: questionDifficultyOptions[0],
                            subTopics: [],
                            tags: [],
                            source: '',
                          },
                        ]);
                      }}
                    >
                      + Add Question
                    </Button>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={3}>
                <CustomGrayContainer>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={4} lg={12}>
                      <FormControl fullWidth>
                        <Typography {...label}>Question Source</Typography>
                        <TextField
                          size="small"
                          sx={inputStyles}
                          value={questionSource}
                          placeholder="Enter Source"
                          disabled={
                            structuredEssayQuestionState.status === 'loading'
                          }
                          onChange={(event) =>
                            setQuestionSource(event.currentTarget.value)
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <FormControl fullWidth>
                        <Typography {...label}>Tags</Typography>
                        <Autocomplete
                          multiple
                          handleHomeEndKeys
                          size="small"
                          sx={inputStyles}
                          disabled={
                            structuredEssayQuestionState.status === 'loading'
                          }
                          inputValue={questionTagsInputValue}
                          onInputChange={(event, value) => {
                            if (event) {
                              const _value = value.toLowerCase();
                              setQuestionTagsInputValue(_value);
                              if (_value.length > 0) {
                                dispatch(
                                  getTagsList({
                                    offset: 0,
                                    limit: 30,
                                    name: _value,
                                  })
                                );
                              }
                            }
                          }}
                          value={questionTags.map((qt) => ({
                            label: qt,
                            value: qt,
                          }))}
                          options={[
                            ...questionTagState.list.map((questionTag) => ({
                              label: questionTag.name,
                              value: questionTag.name,
                            })),
                          ]}
                          onChange={(event, values) => {
                            setQuestionTags(values.map((tag) => tag.value));
                            setQuestionTagsInputValue('');
                          }}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          filterOptions={(options, params: any) => {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const filtered: any = filter(options, params);

                            const { inputValue } = params;
                            const formatedInputValue = inputValue.trim();
                            // Suggest the creation of a new value
                            const isExisting = options.some(
                              (option) => formatedInputValue === option.value
                            );
                            if (formatedInputValue !== '' && !isExisting) {
                              if (!questionTags.includes(formatedInputValue)) {
                                filtered.push({
                                  value: formatedInputValue,
                                  label: `Add "${formatedInputValue}"`,
                                });
                              }
                            }

                            return filtered;
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Select Tags" />
                          )}
                        />
                      </FormControl>
                      <div style={{ paddingTop: '15px' }}>
                        <Typography {...label}>Papers</Typography>
                        <Grid container>
                          {_subTopicData.topic &&
                          _subTopicData.topic.papers &&
                          _subTopicData.topic.papers.length
                            ? _subTopicData.topic.papers.map(
                                (paperItem, index) => {
                                  const isChecked = subTopics.some(
                                    (questionSubTopic) =>
                                      questionSubTopic?.subTopic?._id.toString() ===
                                        UrlParams.subTopicId &&
                                      questionSubTopic.paper === paperItem.name
                                  );

                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={4}
                                      md={3}
                                      lg={6}
                                      key={index}
                                    >
                                      <FormControlLabel
                                        label={paperItem.name}
                                        control={
                                          <Checkbox
                                            checked={isChecked}
                                            onChange={() =>
                                              handleQuestionSubTopicCheckChange(
                                                paperItem
                                              )
                                            }
                                          />
                                        }
                                      />
                                    </Grid>
                                  );
                                }
                              )
                            : null}
                        </Grid>
                      </div>
                      <div style={{ paddingBottom: '15px' }}>
                        {subTopics.filter(filterOutCurrentSubTopic).length ? (
                          <div style={{ paddingTop: '15px' }}>
                            <Typography
                              fontWeight={500}
                              textAlign="center"
                              sx={{ mb: '15px' }}
                            >
                              DUPLICATED QUESTIONS
                            </Typography>
                            <div>
                              {subTopics
                                .filter(filterOutCurrentSubTopic)
                                .map((subtopicListItem, index) => {
                                  const subTopicName =
                                    subtopicListItem &&
                                    subtopicListItem.subTopic &&
                                    subtopicListItem.subTopic.name
                                      ? subtopicListItem.subTopic.name
                                      : '';

                                  const paperName =
                                    subtopicListItem && subtopicListItem.paper
                                      ? subtopicListItem.paper
                                      : '';

                                  return (
                                    <Grid
                                      key={index}
                                      container
                                      spacing={2}
                                      alignItems="center"
                                    >
                                      <Grid item xs={2} sm={11} md={11} lg={10}>
                                        <DetailViewCard
                                          title="Sub Topic"
                                          description={`${subTopicName} - ${paperName}`}
                                          titleProps={{
                                            variant: 'caption',
                                            fontWeight: 600,
                                          }}
                                          descriptionProps={{
                                            variant: 'subtitle2',
                                            className: 'ellipsis-text',
                                          }}
                                          containerProps={{
                                            sx: {
                                              backgroundColor:
                                                'background.paper',
                                              color: 'text.primary',
                                            },
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={2} sm={1} md={1} lg={2}>
                                        <IconButton
                                          color="secondary"
                                          onClick={() => {
                                            setDeleteSubTopicItemAlertData({
                                              open: true,
                                              value:
                                                subtopicListItem.subTopic._id,
                                            });
                                          }}
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                  );
                                })}
                            </div>
                          </div>
                        ) : null}
                        {questionCreatePermission && (
                          <Stack
                            direction="row"
                            justifyContent="center"
                            sx={{ pt: '15px' }}
                          >
                            <Button
                              disabled={
                                structuredEssayQuestionState.status ===
                                'loading'
                              }
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setAddNewSubTopicDrawerVisibility(true);
                              }}
                            >
                              New Subject
                            </Button>
                          </Stack>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </CustomGrayContainer>
              </Grid>
            </Grid>
          </div>
        </div>
        <GenerateQuestionModal
          open={isGenerateQuestionModalOpen}
          subTopicObj={_subTopicData}
          onClose={() => handleIsGenerateQuestionModalOpen(false)}
        />
        <ChangeCriteriaModal
          open={isGenerateQuizChangeCriteriaModalOpen}
          onClose={() => handleIsGenerateQuizChangeCriteriaModalOpen(false)}
        />
        {isAddNewSubTopicDrawerVisible && (
          <AddNewSubTopicDrawer
            open={isAddNewSubTopicDrawerVisible}
            onClose={() => setAddNewSubTopicDrawerVisibility(false)}
            addSubTopic={(questionSubTopics) => {
              const newSubTopics = [...subTopics];

              if (questionSubTopics && questionSubTopics.length) {
                questionSubTopics.forEach((questionSubTopic) => {
                  const isSubTopicExist = newSubTopics.some(
                    (s) =>
                      s.subTopic._id === questionSubTopic.subTopic._id &&
                      s.paper === questionSubTopic.paper
                  );

                  if (!isSubTopicExist) {
                    newSubTopics.push(questionSubTopic);
                  }
                });
              }

              setSubTopics(newSubTopics);
            }}
          />
        )}
        {messageAlertData && messageAlertData.open && (
          <SimpleConfirmationAlert
            open={messageAlertData.open}
            onClose={() => setMessageAlertData(undefined)}
            title={messageAlertData.title || ''}
            description={messageAlertData.description}
            cancelButtonProps={{
              label: 'Close',
              color: 'info',
              onClick: () => setMessageAlertData(undefined),
            }}
          />
        )}
        {deleteQuestionAlertData &&
          deleteQuestionAlertData.open &&
          deleteQuestionAlertData.value && (
            <SimpleConfirmationAlert
              open={deleteQuestionAlertData?.open}
              onClose={() => setDeleteQuestionAlertData(undefined)}
              title="Are you Sure?"
              description="Do you want to delete this question?"
              cancelButtonProps={{
                label: 'Close',
                color: 'info',
                onClick: () => setDeleteQuestionAlertData(undefined),
              }}
              okButtonprops={{
                label: 'Confirm',
                color: 'info',
                onClick: handleDeleteQuestion,
              }}
            />
          )}
        {deleteSubTopicItemAlertData &&
          deleteSubTopicItemAlertData.open &&
          deleteSubTopicItemAlertData.value && (
            <SimpleConfirmationAlert
              open={deleteSubTopicItemAlertData.open}
              onClose={() => setDeleteSubTopicItemAlertData(undefined)}
              title="Are you sure"
              description="Do you want to delete this item?"
              cancelButtonProps={{
                onClick: () => {
                  setDeleteSubTopicItemAlertData(undefined);
                },
                label: 'Cancel',
              }}
              okButtonprops={{
                onClick: () => {
                  setSubTopics((prevSubTopics) =>
                    prevSubTopics.filter(
                      (questionSubTopic) =>
                        questionSubTopic.subTopic._id !==
                        deleteSubTopicItemAlertData.value
                    )
                  );
                  setDeleteSubTopicItemAlertData(undefined);
                },
                color: 'error',
                label: 'Delete',
              }}
            />
          )}
      </div>
    </Fade>
  );
};

export default StructuredEssayQuestions;
